import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Droplets, ArrowUp, ArrowDown } from 'lucide-react';
import { formatNumber, formatSmallPrice, cn } from '../../lib/utils';

export const PoolLiquiditySection = ({ liquidity, dexByPool, arbMode, setArbMode, arbData }) => {
  if (!liquidity?.pools || liquidity.pools.length === 0) return null;
  return (
    <Card data-testid="pool-liquidity">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-violet-500" />
            Pool Liquidity & Prices
          </CardTitle>
          {/* Arbitrage Mode Toggle */}
          <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-1">
            <span className="text-xs text-muted-foreground px-2 hidden sm:inline">Arb Strategy:</span>
            <button
              onClick={() => setArbMode('buy-cheap')}
              data-testid="arb-mode-buy-cheap-button"
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                arbMode === 'buy-cheap'
                  ? "bg-emerald-500 text-white shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              <span className="flex items-center gap-1">
                <ArrowUp className="w-3 h-3" />
                Buy Cheap
              </span>
            </button>
            <button
              onClick={() => setArbMode('sell-expensive')}
              data-testid="arb-mode-sell-expensive-button"
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                arbMode === 'sell-expensive'
                  ? "bg-red-500 text-white shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              <span className="flex items-center gap-1">
                <ArrowDown className="w-3 h-3" />
                Sell Expensive
              </span>
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {liquidity.pools.map((pool) => {
            const isStablePool = pool.token0.symbol === 'USDT' || pool.token0.symbol === 'USDC';
            return (
              <div key={pool.pool_address} className={`p-3 sm:p-5 rounded-xl border-2 ${isStablePool ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-violet-500/30 bg-violet-500/5'}`}>
                <div className="flex items-center justify-between mb-3 sm:mb-4 flex-wrap gap-2">
                  <h3 className="font-bold text-base sm:text-lg">{pool.pair}</h3>
                  <Badge className={`text-xs ${isStablePool ? 'bg-emerald-500/20 text-emerald-600' : 'bg-violet-500/20 text-violet-600'}`}>Fee: {(pool.fee / 10000).toFixed(2)}%</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-3 sm:mb-4">
                  <div className="p-2 sm:p-3 rounded-lg bg-background/50 min-w-0">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1 truncate">{pool.token0.symbol}</p>
                    <p className="text-sm sm:text-lg font-bold font-mono truncate">{formatNumber(parseFloat(pool.token0.balance).toFixed(2))}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground truncate">${formatNumber(parseFloat(pool.token0.balance_usd).toFixed(2))}</p>
                  </div>
                  <div className="p-2 sm:p-3 rounded-lg bg-background/50 min-w-0">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1 truncate">{pool.token1.symbol}</p>
                    <p className="text-sm sm:text-lg font-bold font-mono truncate">{formatNumber(parseFloat(pool.token1.balance).toFixed(0))}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground truncate">${formatNumber(parseFloat(pool.token1.balance_usd).toFixed(2))}</p>
                  </div>
                </div>
                <div className="p-2 sm:p-3 rounded-lg bg-background/80 border">
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground truncate">{pool.token0.symbol}/{pool.token1.symbol}</p>
                      <p className="font-mono text-xs sm:text-sm truncate">{formatNumber(parseFloat(pool.price[`${pool.token0.symbol}_per_${pool.token1.symbol}`]).toFixed(2))}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground truncate">{pool.token1.symbol}/{pool.token0.symbol}</p>
                      <p className="font-mono text-xs sm:text-sm truncate">{parseFloat(pool.price[`${pool.token1.symbol}_per_${pool.token0.symbol}`]).toFixed(8)}</p>
                    </div>
                  </div>
                  {pool.price.krown_usd && (
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-xs text-muted-foreground">KROWN Price (USD)</p>
                      <p className={`font-mono font-bold text-sm sm:text-base ${isStablePool ? 'text-emerald-600' : 'text-violet-600'}`}>{formatSmallPrice(pool.price.krown_usd)}</p>
                    </div>
                  )}
                </div>
                <div className="mt-3 sm:mt-4 flex items-center justify-between flex-wrap gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">Total Liquidity</span>
                  <span className={`font-bold text-base sm:text-lg ${isStablePool ? 'text-emerald-600' : 'text-violet-600'}`}>${formatNumber(parseFloat(pool.liquidity.total_usd).toFixed(2))}</span>
                </div>
                {/* Estimated Fees - based on 24h volume per pool */}
                {dexByPool?.by_pool && (() => {
                  const poolMetrics = dexByPool.by_pool.find(p => p.pool_address?.toLowerCase() === pool.pool_address?.toLowerCase());
                  const poolVolume = poolMetrics?.volume_usd || 0;
                  const estFees = poolVolume * (pool.fee / 1000000);
                  return (
                    <div className="mt-2 pt-2 border-t border-dashed flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Est. 24h Fees</span>
                      <span className="text-xs font-mono text-muted-foreground">
                        ~${formatNumber(estFees.toFixed(2))}
                      </span>
                    </div>
                  );
                })()}
                {/* Arbitrage Opportunity */}
                {arbData && (() => {
                  const poolArb = isStablePool ? arbData.usdtPool : arbData.ethPool;
                  const diffPercent = poolArb.diffPercent;
                  const isExpensive = diffPercent > 0;
                  const tradeUsd = poolArb.amountUsd || 0;
                  const action = poolArb.action;

                  const vsPoolText = isStablePool ? 'vs ETH' : 'vs USDT';

                  // If diff is very small (< 0.01%), show balanced
                  if (Math.abs(diffPercent) < 0.01) {
                    return (
                      <div className="mt-2 pt-2 border-t border-dashed flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground">
                          {diffPercent >= 0 ? '+' : ''}{diffPercent.toFixed(3)}% {vsPoolText}
                        </span>
                        <span className="text-xs font-mono text-emerald-500">
                          ✓ Balanced
                        </span>
                      </div>
                    );
                  }

                  // Format amount display
                  const formatAmount = () => {
                    if (tradeUsd <= 0) return null;
                    if (tradeUsd < 1) return '< $1'; // Very small amount
                    if (isStablePool) {
                      return tradeUsd >= 1000
                        ? `$${formatNumber(tradeUsd.toFixed(0))}`
                        : `$${tradeUsd.toFixed(0)}`;
                    } else {
                      const ethAmount = poolArb.amountEth || 0;
                      if (tradeUsd >= 1000) {
                        return `${ethAmount.toFixed(2)} ETH ($${formatNumber(tradeUsd.toFixed(0))})`;
                      } else if (tradeUsd >= 10) {
                        return `${ethAmount.toFixed(4)} ETH ($${tradeUsd.toFixed(0)})`;
                      }
                      return `$${tradeUsd.toFixed(0)}`;
                    }
                  };

                  // Determine action display based on mode
                  let actionDisplay;
                  const amountStr = formatAmount();

                  if (action === 'buy' && amountStr) {
                    actionDisplay = <span className="text-xs font-mono text-emerald-400">Buy ~{amountStr}</span>;
                  } else if (action === 'sell' && amountStr) {
                    actionDisplay = <span className="text-xs font-mono text-red-400">Sell ~{amountStr}</span>;
                  } else if (action === 'none') {
                    // No action for this pool in current mode
                    actionDisplay = isExpensive
                      ? <span className="text-xs font-mono text-amber-400">Higher price</span>
                      : <span className="text-xs font-mono text-cyan-400">Lower price</span>;
                  }

                  return (
                    <div className="mt-2 pt-2 border-t border-dashed flex items-center justify-between gap-2 flex-wrap">
                      <span className={`text-xs font-medium ${isExpensive ? 'text-red-500' : 'text-emerald-500'}`}>
                        {isExpensive ? '+' : ''}{diffPercent.toFixed(3)}% {vsPoolText}
                      </span>
                      {actionDisplay}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
